import { useEffect, useState } from 'react';
import { placeGaps, type Gap, type RawGaps } from './gaps.ts';
import type { PlacedSegment } from './timeline.ts';

/**
 * Detected pauses for every segment in the block, on the clock.
 *
 * Fetched once per block rather than per window: a half hour yields a few
 * hundred pairs, which is kilobytes, and having them all means the ticks are
 * already drawn wherever the lane is moved to.
 */
export function useSpeechGaps(placed: PlacedSegment[]): Gap[] {
  const [gaps, setGaps] = useState<Gap[]>([]);

  useEffect(() => {
    let cancelled = false;
    const wanted = placed.filter((p) => p.segment.gaps_url);
    if (wanted.length === 0) {
      setGaps([]);
      return;
    }

    void Promise.all(
      wanted.map(async (item) => {
        const response = await fetch(item.segment.gaps_url!, { credentials: 'same-origin' });
        if (!response.ok) return [];
        return placeGaps((await response.json()) as RawGaps, item);
      }),
    )
      .then((lists) => {
        if (cancelled) return;
        // Sorted so the nearest-gap search reads left to right.
        setGaps(lists.flat().sort((a, b) => a.fromMs - b.fromMs));
      })
      // No ticks is a smaller loss than a broken editor.
      .catch(() => undefined);

    return () => { cancelled = true; };
  }, [placed]);

  return gaps;
}
